import React, { useState } from "react";
import Class from "../../../../assets/img/icons/class.svg";
import Exam from "../../../../assets/img/icons/exam.svg";
import Problem from "../../../../assets/img/icons/problem-solving.svg";
import Homework from "../../../../assets/img/icons/homework.svg";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import useReveal from "../../../../hooks/useReveal";
import { Shell } from "../../../shares/landing/LandingWidgets";

/**
 * "آموزشگاه ریاضی مهندس عطار" section.
 *
 * The old cards revealed their full text on :hover only, via
 * `group-hover:hidden` / `group-hover:inline` plus `h-full group-hover:h-0` and
 * `md:hover:scale-150`. Three problems with that:
 *   1. on any touch device there is no hover, so the full text - the actual
 *      substance of this section - was simply unreachable;
 *   2. keyboard users could not reach it either, since nothing was focusable;
 *   3. a card scaling to 150% overlapped its neighbours, which is why the grid
 *      needed a `md:-mb-96` negative margin to paper over the overflow.
 *
 * The four disclosures now read as one structured service ledger instead of
 * four independent cards. All icons use the landing accent token.
 */

const ITEMS = [
    {
        icon: Class,
        title: "کلاس‌ها",
        summary:
            "یادگیری مفهومی و دقیق همه‌ی سرفصل‌های کتاب درسی، با آموزش تشریحی...",
        full: "یادگیری مفهومی و دقیق همه‌ی سرفصل‌های کتاب درسی، با آموزش تشریحی متناسب با پایه دانش‌آموز و تمرکز ویژه بر افزایش مهارت‌های تست‌زنی برای موفقیت در امتحانات و کنکور",
    },
    {
        icon: Exam,
        title: "امتحانات هفتگی",
        summary:
            "در پایان هر جلسه، با توجه به سرفصل‌های تدریس شده در جلسات گذشته...",
        full: "در پایان هر جلسه، با توجه به سرفصل‌های تدریس شده در جلسات گذشته، آزمونی با تعداد سوالات محدود برگزار میشود. پس از آزمون، پاسخنامه‌ی تشریحی در اختیار دانش‌آموزان قرار میگیرد . نتیجه‌ی آزمون شامل درصد دانش‌آموز و میانگین درصد حاضرین، فردای روز کلاس از طریق پیامک برای خانواده‌ها ارسال خواهد شد. این نمرات به‌منظور پایش عملکرد آموزشی دانش‌آموز در طول سال و همچنین به عنوان یکی از معیارهای ثبت‌نام در سال تحصیلی بعد مورد استفاده قرار می‌گیرند .",
    },
    {
        icon: Problem,
        title: "رفع اشکال",
        summary:
            "کلاس‌های رفع اشکال به‌منظور حل مشکلات احتمالی دانش‌آموزان در مرور...",
        full: "کلاس‌های رفع اشکال به‌منظور حل مشکلات احتمالی دانش‌آموزان در مرور جزوات، حل تمرین‌ها و تست‌زنی، برگزار می‌شوند. این جلسات در روزهای مشخصی از هفته، با اطلاع‌رسانی قبلی توسط دانش‌آموزان و خانواده‌ها با موسسه، قابل استفاده هستند. جلسات رفع اشکال توسط دانشآموزان فارغ‌التحصیل دوره‌های پیشین موسسه که تسلط کافی بر مباحث تدریس شده دارند، برگزار می‌گردد. دانش‌آموزان با هماهنگی قبلی می‌توانند به‌صورت انفرادی در این جلسات شرکت کرده و سوالات خود را مطرح کنند. این خدمات ویژه برای دانش‌آموزان موسسه کاملاً رایگان ارائه می‌شود.",
    },
    {
        icon: Homework,
        title: "تکلیف",
        summary:
            "هر هفته تعدادی سوال به عنوان تکلیف هفتگی در سایت بارگذاری ...",
        full: "هر هفته تعدادی سوال به عنوان تکلیف هفتگی در سایت بارگذاری می‌شود دانش آموزان موظف‌اند این تکالیف را تا موعد مقرر به صورت تشریحی حل کرده و از طریق سایت ارسال کنند. در روز کلاس تکالیف تحویل داده شده توسط مسئولین بررسی و تصحیح می‌شوند نمره‌ی تکالیف در روز بعد از کلاس از طریق پیامک به خانواده‌ها اطلاع داده خواهد شد.",
    },
];

function FeatureCard({ item, index }) {
    const [open, setOpen] = useState(false);
    const bodyId = `feature-body-${index}`;

    return (
        <article
            className="landing-feature-row reveal reveal--up group grid gap-md p-xl text-start sm:grid-cols-[5.6rem_minmax(0,1fr)_4rem] sm:items-start max-sm:grid-cols-[4.8rem_minmax(0,1fr)]"
            style={{ "--i": index }}
        >
            <span
                aria-hidden="true"
                className="inline-flex h-[5.6rem] w-[5.6rem] items-center justify-center rounded-card bg-primary-soft text-primary max-sm:h-[4.8rem] max-sm:w-[4.8rem]"
            >
                <img
                    src={item.icon}
                    alt=""
                    className="h-[2.8rem] w-[2.8rem] object-contain"
                />
            </span>

            <div className="min-w-0">
                <h3 className="text-lg font-bold text-ink-strong">
                    {item.title}
                </h3>
                <p
                    id={bodyId}
                    className="mt-xs max-w-[68ch] text-base font-light leading-[2] text-ink-muted"
                >
                    {open ? item.full : item.summary}
                </p>
            </div>

            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-controls={bodyId}
                aria-label={item.title}
                className="col-start-2 flex h-[3.6rem] w-[3.6rem] shrink-0 items-center justify-center justify-self-end rounded-soft border border-rule-subtle bg-surface text-primary transition-colors duration-150 hover:border-primary hover:bg-primary-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:col-auto sm:justify-self-auto motion-reduce:transition-none"
            >
                <ExpandMoreRoundedIcon
                    sx={{ fontSize: 22 }}
                    className={`transition-transform duration-200 motion-reduce:transition-none ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </button>
        </article>
    );
}

const Features = () => {
    const [ref, inView] = useReveal();

    return (
        <section
            id="features"
            ref={ref}
            className={`features ${inView ? "in-view" : ""} py-7xl max-sm:py-6xl`}
        >
            <Shell>
                <div className="grid items-start gap-5xl lg:grid-cols-[minmax(24rem,0.72fr)_minmax(0,1.28fr)]">
                    <div className="min-w-0 lg:sticky lg:top-[10rem]">
                        <h2
                            className="reveal reveal--right text-4xl font-bold leading-tight text-ink-strong max-sm:text-3xl"
                            style={{ "--i": 0 }}
                        >
                            آموزشگاه ریاضی مهندس عطار
                        </h2>
                        <p
                            className="reveal reveal--right mt-2xl max-w-[64ch] text-lg font-light leading-[2.1] text-ink-muted"
                            style={{ "--i": 1 }}
                        >
                            در این مجموعه، تلاش میکنیم تا شرایط مناسبی برای یادگیری درس
                            ریاضیات فراهم کنیم. با توجه به اهمیت امتحانات پایانی و کنکور
                            سراسری، ضروری است که دانش‌آموزان علاوه بر یادگیری مفاهیم اصلی و
                            روشهای تشریحی، مهارت خود را در تست‌زنی نیز تقویت کنند . البته
                            این هدف زمانی محقق میشود که دانش‌آموز زمان کافی برای مطالعه
                            ریاضی و حل مسائل آن اختصاص دهد. نباید فراموش کنیم که واژه
                            "ریاضی" از واژه "ریاضت" گرفته شده و به معنای تلاش و کوشش فراوان
                            است؛ بنابراین موفقیت در این درس نیازمند پشتکار، تمرین مداوم و
                            جدیت در یادگیری است.
                        </p>
                    </div>

                    <div className="divide-y divide-rule-subtle overflow-hidden rounded-wide border border-rule-subtle bg-surface shadow-card">
                        {ITEMS.map((item, index) => (
                            <FeatureCard
                                key={item.title}
                                item={item}
                                index={index + 1}
                            />
                        ))}
                    </div>
                </div>
            </Shell>
        </section>
    );
};

export default Features;
